<template>
  <div
    class="cart-line__product"
  >
    <div class="cart-line__product-visual">
      <ProductLabelSale
        v-if="item.product?.product?.isSale && item.product?.product?.discount >= 40"
        :prices="totalPrice"
      />
      <AppLink :to="item.product.product.url">
        <img
          :src="image"
          :alt="item.product.name"
        >
      </AppLink>
      <div
        v-if="editable"
        class="cart-line__product-delete-text"
        @click.prevent="removeItem"
      >
        <span class="">{{ $t('Remove') }}</span>
      </div>
    </div>
    <div class="cart-line__product-content">
      <div
        v-if="item.product?.product?.brand"
        class="cart-line__product-brand"
      >
        <AppLink :to="item.product?.product?.url">
          <span class="product-item__brand heading">
            {{ item.product?.product?.brand }}
          </span>
        </AppLink>
      </div>
      <div
        v-if="item.product?.name"
        class="cart-line__product-name"
        :class="[{ 'item-giftcard product-item__brand': isGiftcard }]"
      >
        <AppLink :to="item.product?.product?.url">
          <span>
            {{ item.product.name.trim().replace(new RegExp(`\\s*${item.product?.product?.brand}\\s*$`), '') }}
            {{ item?.product?.product?.season ? ` - ${item?.product?.product?.season}` : '' }}
          </span>
        </AppLink>
      </div>
      <table
        v-if="item?.configurable_options"
        class="cart-line__product-configurable-options"
      >
        <tr
          v-for="(option, index) in item.configurable_options"
          :key="index"
        >
          <td>
            <span class="label">
              {{ option?.option_label }}:
            </span>
            <span>
              {{ option?.value_label }}
            </span>
          </td>
        </tr>
      </table>
      <div
        v-if="item.product?.sku"
        class="cart-line__product-sku"
      >
        <span class="product-item__sku">
          <span>{{ $t('Art.Number: {sku}', { sku: item.product?.sku || '' }) }}</span>
        </span>
      </div>
    </div>
    <div class="cart-line__product-attribute">
      <table
        v-if="item?.configurable_options"
        class="cart-line__product-data"
      >
        <tr
          v-for="(option, index) in item.configurable_options"
          :key="index"
        >
          <td>
            <span class="label">
              {{ option?.option_label }}:
            </span>
            <span>
              {{ option?.value_label }}
            </span>
          </td>
        </tr>
      </table>
    </div>
    <div
      v-if="editable"
      class="cart-line__product-quantity"
    >
      <select
        v-if="quantity <= threshold && quantity % qtyIncrements === 0"
        :value="quantity"
        :disabled="pending || !maxSaleQty"
        @change="updateItem(parseInt($event.target.value))"
      >
        <option
          v-for="q in quantityList"
          :key="q"
          :value="q"
          v-text="q"
        />
        <option
          v-if="threshold <= maxSaleQty"
          :value="threshold + qtyIncrements"
        >
          {{ $t('More than {number}', { number: threshold }) }}
        </option>
      </select>
      <input
        v-else
        type="number"
        class="cart-line__product-quantity"
        :step="qtyIncrements"
        :maxlength="maxSaleQty"
        :value="quantity"
        @change="updateItem(parseInt($event.target.value))"
      >
    </div>
    <div
      v-else
      class="cart-line__product-quantity"
    >
      <div class="cart-line__quantity">
        {{ $t('Quantity') }}: {{ quantity }}
      </div>
    </div>
    <div
      class="cart-line__product-price"
    >
      <ProductPrice
        class="cart-line__product-totalprice"
        :prices="totalPrice"
        show-currency
      />
    </div>
    <div
      v-if="editable"
      class="cart-line__product-delete"
    >
      <button
        class="cart-line__product-delete-icon"
        @click.prevent="removeItem"
      >
        <IconTrash class="icon icon--trash" />
        <span class="visually-hidden">{{ $t('Remove') }}</span>
      </button>
    </div>
    <div
      v-if="hasErrors"
      class="cart-line__product-error"
    >
      <p
        v-for="(error, index) in item.errors"
        :key="`error-${index}`"
        v-text="error.message"
      />
    </div>
    <div v-if="isPersonalized">
      <CartCustomizableOptions
        v-if="editable"
        class="cart-line__personalize"
        :item="item"
      >
        <template
          v-if="extraCosts.finalPrice / item.quantity"
          #price
        >
          <span class="cart-line__personalize-price">
            + <ProductPrice
              :hide-discount="true"
              :prices="{
                regularPrice: (extraCosts.regularPrice || 0) / item.quantity,
                finalPrice: extraCosts.finalPrice / item.quantity,
              }"
            />
          </span>
        </template>
      </CartCustomizableOptions>
    </div>
    <CartGiftCardOptions
      v-if="isGiftcard"
      class="cart-line__options"
      :item="item"
    />
  </div>
</template>

<script lang="ts" setup>
import type { MagentoCartItem } from '~layers/app/types/magento'
import AppLink from '~layers/app/components/App/Link.vue'

export interface Props {
  item: MagentoCartItem
  editable?: boolean
}
const props = withDefaults(defineProps<Props>(), { editable: false })
const { item } = toRefs(props)
const { updateCartItem, removeCartItem, addedToCart, pending, loadDeliveryPromiseData } = useCart()
const { public: config } = useRuntimeConfig()
const threshold = ref<number>(10)
const quantity = ref<number>(item.value?.quantity || 0)

const hasErrors = computed<boolean>(() =>
  !!item.value?.errors,
)

const simpleProduct = computed(() =>
  item.value.configured_variant || item.value.product,
)

const isPersonalized = computed<boolean>(() => !!item.value?.customizable_options?.length)
const isGiftcard = computed<boolean>(() => item.value?.product?.__typename === 'GiftCardProduct')

const extraCosts = computed(() => {
  return {
    regularPrice: null,
    finalPrice: item.value.prices.row_total_including_tax.value - (simpleProduct.value?.price_range?.minimum_price?.final_price?.value * item.value.quantity),
  }
})

const totalPrice = computed(() => {
  const rowTotal = item.value.prices?.row_total_including_tax?.value || 0
  const finalPricePerItem = item.value.prices?.price_including_tax.value || simpleProduct.value?.price_range?.minimum_price?.final_price?.value || 0 || 0
  const regularPricePerItem = simpleProduct.value?.price_range?.minimum_price?.regular_price?.value || 0
  const qty = item.value.quantity || 0

  const isNoDiscount = rowTotal === regularPricePerItem * qty

  return {
    finalPrice: isNoDiscount ? rowTotal : finalPricePerItem * qty,
    regularPrice: isNoDiscount ? rowTotal : regularPricePerItem * qty,
  }
})

const image = computed<string>(() => {
  if (simpleProduct.value?.image?.url && !simpleProduct.value?.image?.url.includes('placeholder')) {
    return getProductImage(simpleProduct.value.image.url, 'f=rs:fit:400:400')
  }
  if (item.value.product.image?.url && !item.value.product.image?.url.includes('placeholder')) {
    return getProductImage(item.value.product.image.url, 'f=rs:fit:400:400')
  }
  return `${config.xcdnUrl}/media/catalog/product/placeholder/default/Product.FallBack.jpg`
})

const configuredMaxSaleQty = computed<number>(() =>
  simpleProduct.value?.stockItem?.max_sale_qty || 1000,
)

const maxSaleQty = computed<number>(() => {
  if (simpleProduct.value?.stockItem?.backorders && simpleProduct.value?.stockItem?.max_sale_qty === 0) {
    return 1000
  }
  let _maxSaleQty = configuredMaxSaleQty.value
  if (simpleProduct.value?.stockItem?.use_config_max_sale_qty === 1 && configuredMaxSaleQty.value === 0) {
    _maxSaleQty = 10000
  }
  return Math.min(Math.min(_maxSaleQty, 10000), simpleProduct.value?.stock_left || 10000)
})

const qtyIncrements = computed<number>(() =>
  simpleProduct.value?.stockItem?.qty_increments || 1,
)

const quantityList = computed<number[]>(() => {
  const maxAmount = Math.min(threshold.value, maxSaleQty.value)
  const max = Math.floor(maxAmount / qtyIncrements.value)
  return [...Array(max).keys()].map(i => (i + 1) * qtyIncrements.value)
})

const { removeFromCart } = useDataLayer()
const updateItem = async (inputValue?: number) => {
  if (!isNaN(inputValue)) {
    quantity.value = parseInt(inputValue)
  }
  if (quantity.value > maxSaleQty.value) {
    quantity.value = maxSaleQty.value
  }
  const oldQty = item.value.quantity
  await updateCartItem({
    cart_item_id: +item.value.id,
    quantity: quantity.value,
  }, true)
  if (addedToCart.value.length > 0) {
    quantity.value = +item.value.quantity
  }
  const newQty = item.value.quantity
  if (oldQty > newQty) {
    removeFromCart({ ...item.value, quantity: oldQty - newQty })
  }
}

const removeItem = async () => {
  await removeCartItem(+item.value.id)
  removeFromCart(item.value)
  loadDeliveryPromiseData()
}
</script>

<style src="~layers/app/components/Cart/Line.css" />
